import type { ReactNode } from 'react';
import type { Observable } from 'rxjs';

export enum SelectType {
    single = 'single',
    multi = 'multi',
}

export enum CompletionItemType {
    option = 'option',
    section = 'section',
    group = 'group',
    button = 'button',
    search = 'search',
}

export interface ICompletionItem {
    type: CompletionItemType;
    name: string;
    displayName?: React.ReactNode;
    key?: string;
    className?: string;
    dataTestId?: string;
    startingIcon?: ReactNode;
}

export interface CompletionOption extends ICompletionItem {
    type: CompletionItemType.option;
    optionType: string | symbol;
    id: string;
    disabled?: boolean;
    detail?: string;
    altName?: string;
    endingIcon?: ReactNode;
    tooltip?: ReactNode;
}

export interface CompletionSection extends ICompletionItem {
    type: CompletionItemType.section;
}

export interface CompletionGroup extends ICompletionItem {
    type: CompletionItemType.group;
    items: CompletionItem[] | Observable<CompletionItem[]>;
}

export interface CompletionButton extends ICompletionItem {
    type: CompletionItemType.button;
    ref?: React.RefObject<HTMLDivElement>;
    onClick: () => void;

    /**
     * If true, the list will be closed when the button is clicked,
     * provided it is not a multi-select list, in which case you will have to
     * manually close the list by clicking outside of it.
     * @default true
     * */
    closeListOnClick?: boolean;

    endingIcon?: ReactNode;
}

export interface CompletionSearch extends ICompletionItem {
    type: CompletionItemType.search;
    onSearchChanged: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}

export type CompletionItem =
    | CompletionOption
    | CompletionSection
    | CompletionGroup
    | CompletionButton
    | CompletionSearch;

export enum FocusStatus {
    /**
     * The item is focused with the keyboard and has a focused styling
     */
    focused = 'focused',
    /**
     * The item has a child element open that does not require focus
     * i.e. if the item is a option, its tooltip is open
     */
    expanded = 'expanded',
    /**
     * The item has a child element open that is now in focus
     * i.e. if the item is a group, its child list is open and has a focused item
     */
    active = 'active',
}

export type ListFocusStatus = { index: number; status: FocusStatus };
